import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Checkbox from '@mui/material/Checkbox';
import { CircularProgress, Box, MenuItem, Select, Typography, InputLabel } from '@mui/material';
import {
  selectSignupData,
  selectSignupError,
  signupUser,
  SignupAppDispatch,
  selectSignupIsLoading,
  fetchSignupFields,
  selectSignupFields,
  selectSignupFieldsLoading
} from '../../modules/signup';
import {
  PageContainer,
  HeroPanel,
  HeroImage,
  HeroBranding,
  FormPanel,
  FormContainer,
  StyledTextField,
  StyledFormControl,
  SubmitButton,
  FormBox,
  ErrorMessage,
  RequiredAsterisk,
  Subtext
} from './styles';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AlertPopup from '../../components/common/AlertPopup';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import heroImage from '../../assets/facility/signup-background.jpg';
import Logo from '../../assets/main/logo-white.png';

const SignupPage: React.FC = () => {
  const dispatch = useDispatch<SignupAppDispatch>();
  const signupData = useSelector(selectSignupData);
  const error = useSelector(selectSignupError);
  const isLoading = useSelector(selectSignupIsLoading);
  const fields = useSelector(selectSignupFields);
  const isFieldsLoading = useSelector(selectSignupFieldsLoading);
  
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [alert, setAlert] = useState<{message: string, severity: 'success' | 'error'} | null>(null);

  useEffect(() => {
    dispatch(fetchSignupFields());
  }, [dispatch]);

  // Dynamically build schema
  const schema = useMemo(() => {
    const shape: any = {
      first_name: yup.string().required('First name is required'),
      last_name: yup.string().required('Last name is required'),
      email: yup.string().email('Invalid email address').required('Email is required'),
      phone_number: yup.string().required('Phone number is required'),
      org_name: yup.string().required('Organization name is required'),
      address: yup.string().required('Address is required'),
      password: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
      confirm_password: yup.string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Please confirm your password'),
      agreeToPlatform: yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
    };

    const fieldsArray = Array.isArray(fields) ? fields : [];
    fieldsArray.forEach((field: any) => {
      let validator: any;
      
      if (field.field_type === 'file') {
        validator = yup.mixed();
        if (field.is_expiry_required) {
          shape[`${field.name}_expiry`] = yup.string().required('Expiry date is required');
        }
        if (field.is_issue_date_required) {
          shape[`${field.name}_issue`] = yup.string().required('Issue date is required');
        }
      } else {
        validator = yup.string();
      }

      if (field.is_required) {
        validator = validator.required(`${field.label} is required`);
      }
      shape[field.name] = validator;
    });

    return yup.object().shape(shape);
  }, [fields]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      agreeToPlatform: false,
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      org_name: '',
      address: '',
      password: '',
      confirm_password: '',
    }
  });

  // Re-initialize form when fields are loaded
  useEffect(() => {
    if (Array.isArray(fields)) {
      const defaults: any = { 
        agreeToPlatform: false,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        org_name: '',
        address: '',
        password: '',
        confirm_password: '',
      };
      fields.forEach((f: any) => {
        defaults[f.name] = '';
        if (f.field_type === 'file') {
          if (f.is_expiry_required) defaults[`${f.name}_expiry`] = '';
          if (f.is_issue_date_required) defaults[`${f.name}_issue`] = '';
        }
      });
      reset(defaults);
    }
  }, [fields, reset]);

  const onSubmit = (data: any) => {
    // Check if passwords match if confirm_password exists
    if (data.password && data.confirm_password && data.password !== data.confirm_password) {
      setAlert({ message: 'Passwords do not match', severity: 'error' });
      return;
    }

    const userData = {
      ...data,
      user_type: 'org_admin',
    };
    dispatch(signupUser(userData));
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (signupData) {
      setAlert({ message: 'Signup successful!', severity: 'success' });
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
  }, [signupData, navigate]);

  useEffect(() => {
    if (error) {
      setAlert({ message: error, severity: 'error' });
    }
  }, [error]);

  const renderField = (field: any) => {
    const errorMsg = (errors as any)[field.name]?.message;

    switch (field.field_type) {
      case 'password':
        return (
          <Box mb={3} key={field.name}>
            <InputLabel htmlFor={field.name} sx={{ mb: 1, fontWeight: 500 }}>
              {field.label}<RequiredAsterisk>*</RequiredAsterisk>
            </InputLabel>
            <Box sx={{ position: 'relative' }}>
              <StyledTextField
                fullWidth
                {...register(field.name)}
                id={field.name}
                type={showPassword ? 'text' : 'password'}
                placeholder={`Enter ${field.label}`}
                error={!!errorMsg}
              />
              <Box 
                onClick={togglePasswordVisibility}
                sx={{ 
                  position: 'absolute', 
                  right: 14, 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  cursor: 'pointer',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 1
                }}
              >
                {showPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
              </Box>
            </Box>
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
          </Box>
        );

      case 'select':
        const options = field.options ? field.options.split(',').map((o: string) => o.trim()) : [];
        return (
          <Box mb={3} key={field.name}>
            <InputLabel htmlFor={field.name} sx={{ mb: 1, fontWeight: 500 }}>
              {field.label}{field.is_required && <RequiredAsterisk>*</RequiredAsterisk>}
            </InputLabel>
            <StyledFormControl fullWidth error={!!errorMsg}>
              <Controller
                name={field.name}
                control={control}
                render={({ field: selectField }) => (
                  <Select {...selectField} id={field.name} value={selectField.value || ''} displayEmpty>
                    <MenuItem value="" disabled sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Select {field.label}
                    </MenuItem>
                    {options.map((opt: string, idx: number) => (
                      <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </StyledFormControl>
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
          </Box>
        );

      case 'file':
        return (
          <Box mb={3} key={field.name} sx={{ gridColumn: 'span 2', p: 2.5, border: '1px solid #E6E8F0', borderRadius: '12px', bgcolor: '#F9FAFB' }}>
            <InputLabel sx={{ mb: 1.5, fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
              {field.label}{field.is_required && <RequiredAsterisk>*</RequiredAsterisk>}
            </InputLabel>
            <Controller
              name={field.name}
              control={control}
              render={({ field: { onChange, value } }) => {
                const inputRef = React.createRef<HTMLInputElement>();
                return (
                  <Box>
                    <Box 
                      onClick={() => inputRef.current?.click()}
                      sx={{ 
                        border: '2px dashed #E5E7EB', 
                        p: 3, 
                        textAlign: 'center', 
                        borderRadius: '10px',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#5E8E7E',
                          backgroundColor: '#F3F4F6'
                        }
                      }}
                    >
                      <CloudUploadIcon sx={{ color: '#5E8E7E', mb: 1, fontSize: 32 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                        {value ? `Selected: ${value.name}` : `Click to upload ${field.document_type_name || field.label}`}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">Format: PDF, PNG, JPG</Typography>
                      <input
                        type="file"
                        hidden
                        ref={inputRef}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            onChange(e.target.files[0]);
                          }
                        }}
                        accept=".pdf,.png,.jpg,.jpeg"
                      />
                    </Box>
                    
                    {/* Date Fields if required */}
                    {(field.is_issue_date_required || field.is_expiry_required) && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 2.5 }}>
                        {field.is_issue_date_required && (
                          <Box sx={{ flex: 1 }}>
                            <InputLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500, color: '#4B5563' }}>Issue Date</InputLabel>
                            <StyledTextField
                              fullWidth
                              type="date"
                              {...register(`${field.name}_issue`)}
                              error={!!(errors as any)[`${field.name}_issue`]}
                            />
                            {(errors as any)[`${field.name}_issue`] && (
                              <ErrorMessage>{(errors as any)[`${field.name}_issue`].message}</ErrorMessage>
                            )}
                          </Box>
                        )}
                        {field.is_expiry_required && (
                          <Box sx={{ flex: 1 }}>
                            <InputLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500, color: '#4B5563' }}>Expiry Date</InputLabel>
                            <StyledTextField
                              fullWidth
                              type="date"
                              {...register(`${field.name}_expiry`)}
                              error={!!(errors as any)[`${field.name}_expiry`]}
                            />
                            {(errors as any)[`${field.name}_expiry`] && (
                              <ErrorMessage>{(errors as any)[`${field.name}_expiry`].message}</ErrorMessage>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              }}
            />
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
          </Box>
        );

      case 'textarea':
        return (
          <Box mb={3} key={field.name}>
            <InputLabel htmlFor={field.name} sx={{ mb: 1, fontWeight: 500 }}>
              {field.label}{field.is_required && <RequiredAsterisk>*</RequiredAsterisk>}
            </InputLabel>
            <StyledTextField 
              fullWidth
              {...register(field.name)} 
              id={field.name} 
              multiline
              rows={3}
              placeholder={`Enter ${field.label}`}
              error={!!errorMsg}
            />
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
          </Box>
        );

      default:
        return (
          <Box mb={3} key={field.name}>
            <InputLabel htmlFor={field.name} sx={{ mb: 1, fontWeight: 500 }}>
              {field.label}{field.is_required && <RequiredAsterisk>*</RequiredAsterisk>}
            </InputLabel>
            <StyledTextField 
              fullWidth
              {...register(field.name)} 
              id={field.name} 
              type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'} 
              placeholder={`Enter ${field.label}`} 
              error={!!errorMsg}
            />
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
          </Box>
        );
    }
  };

  if (isFieldsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={40} sx={{ color: '#5E8E7E' }} />
      </Box>
    );
  }

  return (
    <PageContainer>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      
      {/* Left Panel - Hero Image */}
      <HeroPanel>
        <HeroImage src={heroImage} alt="Organization signup background" />
        <HeroBranding>
          <Box>
            <img src={Logo} alt="TalentHub logo" />
          </Box>
        </HeroBranding>
      </HeroPanel>

      {/* Right Panel - Form */}
      <FormPanel>
        <FormContainer>
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Register Your Organization
            </Typography>
            <Subtext>
              Join our platform to streamline your staffing process and manage healthcare facilities efficiently.
            </Subtext>
          </Box>

          <FormBox onSubmit={handleSubmit(onSubmit)}>
            {/* Core User Fields */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                {renderField({ name: 'first_name', label: 'First Name', field_type: 'text', is_required: true })}
              </Box>
              <Box sx={{ flex: 1 }}>
                {renderField({ name: 'last_name', label: 'Last Name', field_type: 'text', is_required: true })}
              </Box>
            </Box>
            
            {renderField({ name: 'email', label: 'Email Address', field_type: 'text', is_required: true })}
            {renderField({ name: 'phone_number', label: 'Phone Number', field_type: 'text', is_required: true })}
            {renderField({ name: 'org_name', label: 'Organization Name', field_type: 'text', is_required: true })}
            {renderField({ name: 'address', label: 'Address', field_type: 'text', is_required: true })}

            {/* Dynamic Fields from API */}
            {Array.isArray(fields) && fields.map(field => renderField(field))}

            {/* Passwords */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                {renderField({ name: 'password', label: 'Password', field_type: 'password', is_required: true })}
              </Box>
              <Box sx={{ flex: 1 }}>
                {renderField({ name: 'confirm_password', label: 'Confirm Password', field_type: 'password', is_required: true })}
              </Box>
            </Box>

            <Box mt={2}>
              <Controller
                name="agreeToPlatform"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox {...field} checked={field.value} size="small" />
                    <Typography variant="body2" sx={{ color: errors.agreeToPlatform ? 'error.main' : 'text.secondary' }}>
                      I agree to the platform terms and conditions
                    </Typography>
                  </Box>
                )}
              />
              {errors.agreeToPlatform && <ErrorMessage>{(errors.agreeToPlatform as any).message}</ErrorMessage>}
            </Box>

            <SubmitButton
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
            </SubmitButton>

            <Box mt={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Already have an account? <a href="/" style={{ color: '#5E8E7E', fontWeight: 600, textDecoration: 'none' }}>Login here</a>
              </Typography>
            </Box>
          </FormBox>
        </FormContainer>
      </FormPanel>
    </PageContainer>
  );
};

export default SignupPage;